import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {CookieService} from 'ngx-cookie-service';
import {ApiConnectService} from './api-connect.service';
import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {NotificationsService} from './notifications.service';
import {UserService} from "./user.service";
import {UiLocaleService} from './ui-locale.service';
import {User} from '../models/user.model';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';

const SESSION_EXPIRED_KEY = 'SESSION_EXPIRED';
const TRUE_VALUE = 'true';
const BASIC_AUTH_PREFIX = 'Basic ';
const CONTENT_TYPE_HEADER = 'Content-Type';
const JSON_CONTENT_TYPE = 'application/json';
const REQUESTED_WITH_HEADER = 'X-Requested-With';
const XML_HTTP_REQUEST = 'XMLHttpRequest';
const AUTHORIZATION_HEADER = 'Authorization';
const OFFSET_HEADER = 'offset';
const RESPONSE_OBSERVE = 'response';
const CREDENTIAL_SEPARATOR = ':';
const LANGUAGE_STORAGE_KEY = 'lang';

export interface PasswordRecoveryRequestResponse {
  emailHint: string | null;
}

@Injectable()
export class AuthService {
  private sessionValidated = false;
  private readonly sessionCleared$ = new Subject<void>();
  readonly onSessionCleared$ = this.sessionCleared$.asObservable();

  constructor(private http: HttpClient,
              private router: Router,
              private cookieService: CookieService,
              private notificationsService: NotificationsService,
              private apiURL: ApiConnectService,
              private userService: UserService,
              private uiLocaleService: UiLocaleService) {
  }

  isAuthenticated() {
    if (localStorage.getItem('Authenticated') === null) {
      localStorage.setItem('Authenticated', 'false');
      return false;
    } else if (localStorage.getItem('Authenticated') === 'false') {
      return false;
    } else if (localStorage.getItem('Authenticated') === 'true') {
      if (!this.hasLocalUser()) {
        localStorage.setItem('Authenticated', 'false');
        return false;
      }
      return true;
    }

    localStorage.setItem('Authenticated', 'false');
    return false;
  }

  validateAuthenticatedSession(): boolean | Observable<boolean> {
    if (!this.isAuthenticated()) {
      return false;
    }
    if (this.sessionValidated) {
      return true;
    }
    return this.http.get<User>(this.apiURL.restoreSession, {observe: 'response', withCredentials: true})
      .mergeMap((response: HttpResponse<User>) => this.withServerLocale(response.body))
      .map(user => {
        if (user) {
          this.uiLocaleService.applyServerLocale(user.uiLocale, user.language);
          this.userService.setLocal(this.toLocalUser(user));
        }
        this.sessionValidated = true;
        return true;
      })
      .catch(() => {
        this.clearClientSession();
        this.router.navigate(['/signin']);
        return Observable.of(false);
      });
  }

  signIn(login, password) {
    this.sessionValidated = false;
    const authorization: string = BASIC_AUTH_PREFIX + btoa(login + CREDENTIAL_SEPARATOR + password);
    const headers = new HttpHeaders({
      [CONTENT_TYPE_HEADER]: JSON_CONTENT_TYPE,
      [REQUESTED_WITH_HEADER]: XML_HTTP_REQUEST,
      [AUTHORIZATION_HEADER]: authorization,
      [OFFSET_HEADER]: this.userService.getTimezoneOffset()
    });

    return this.http.post<User>(this.apiURL.logIn, null,
      {headers: headers, observe: RESPONSE_OBSERVE, withCredentials: true})
      .mergeMap((response: HttpResponse<User>) => this.withServerLocale(response.body)
        .map(user => {
          this.uiLocaleService.applyServerLocale(user && user.uiLocale, user && user.language);
          this.sessionValidated = true;
          return this.withResponseBody(response, user);
        }));
  }

  isServerSessionConfirmed(): boolean {
    return this.sessionValidated;
  }

  changePass(login, oldPassword, newPassword) {
    return this.http.post(this.apiURL.changePass + '?' + new HttpParams().set("login", login).set("oldPassword",  oldPassword).set("newPassword",  newPassword), { observe: 'response' });
  }

  requestPasswordRecovery(login: string): Observable<PasswordRecoveryRequestResponse> {
    return this.http.post<PasswordRecoveryRequestResponse>(this.apiURL.requestPasswordRecovery, {login});
  }

  confirmPasswordRecovery(token: string, password: string): Observable<void> {
    return this.http.post<void>(this.apiURL.confirmPasswordRecovery, {token, password});
  }

  signOut() {
    this.sessionValidated = false;
    this.http.post(this.apiURL.logOut, null, {withCredentials: true})
      .subscribe(() => {}, () => {});

    this.clearClientSession();
    this.notificationsService.clearNearNotification();
    this.router.navigate(['/signin']);
  }

  clearClientSession(): void {
    this.sessionValidated = false;
    this.sessionCleared$.next();
    this.clearStoredSession(localStorage);
    this.cookieService.deleteAll();
  }

  private clearStoredSession(storage: Storage): void {
    const language = storage.getItem(LANGUAGE_STORAGE_KEY);
    storage.clear();
    if (language !== null) { storage.setItem(LANGUAGE_STORAGE_KEY, language); }
  }

  rememberSessionExpired(): void {
    sessionStorage.setItem(SESSION_EXPIRED_KEY, TRUE_VALUE);
  }

  consumeSessionExpired(): boolean {
    const expired = sessionStorage.getItem(SESSION_EXPIRED_KEY) === TRUE_VALUE;
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    return expired;
  }

  private hasLocalUser(): boolean {
    const rawUser = localStorage.getItem('User');
    if (rawUser === null) {
      return false;
    }
    try {
      return JSON.parse(rawUser) !== null;
    } catch (error) {
      return false;
    }
  }

  private withServerLocale(user: User): Observable<User> {
    if (!user) {
      return Observable.of(user);
    }
    return this.http.get<User>(this.apiURL.getSelf, {observe: 'response', withCredentials: true})
      .map((response: HttpResponse<User>) => Object.assign({}, user, {
        uiLocale: response.body ? response.body.uiLocale : user.uiLocale,
        language: response.body && response.body.language ? response.body.language : user.language
      }))
      .catch(() => Observable.of(user));
  }

  private withResponseBody(response: HttpResponse<User>, body: User): HttpResponse<User> {
    if (response && typeof response.clone === 'function') {
      return response.clone({body: body});
    }
    return Object.assign({}, response, {body: body});
  }

  private toLocalUser(user: any): any {
    const localUser = Object.assign({}, user);
    localUser.roles = this.normalizeRoles(user.roles);
    localUser.role = this.resolvePrimaryRole(localUser.roles);
    return localUser;
  }

  private normalizeRoles(roles: any[]): string[] {
    if (!roles) {
      return [];
    }
    return roles.map(role => {
      const roleValue = String(role);
      return roleValue.indexOf('ROLE_') === 0 ? roleValue : 'ROLE_' + roleValue;
    });
  }

  private resolvePrimaryRole(roles: string[]): string {
    const priority = ['ROLE_ADMIN', 'ROLE_HEAD', 'ROLE_PARTNER', 'ROLE_USER'];
    for (let index = 0; index < priority.length; index++) {
      if (roles.indexOf(priority[index]) !== -1) {
        return priority[index];
      }
    }
    return roles.length > 0 ? roles[0] : null;
  }

}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/auth.service.ts